// Lead Generation System — staff Lead Generator roster API client. Thin typed
// wrappers over `api/lead-generators` (reads gate on LeadGen.View; mutations on
// LeadGen.Manage). A Lead Generator is an external portal contractor who sources
// Preleads; they are NOT a broker and hold no Phoenix staff permissions.

import { api } from "@/api/client";
import type { PagedQuery, PagedResult } from "@/types/api";

export type LeadGeneratorStatus = "Invited" | "Active" | "Suspended" | "Inactive";

export interface LeadGeneratorSummaryDto {
  id: string;
  displayName: string;
  email: string;
  phone?: string | null;
  status: LeadGeneratorStatus;
  /** The linked external portal login, once provisioned. */
  portalUserId?: string | null;
  hiredAtUtc?: string | null;
  isArchived: boolean;
  createdAt: string;
  /** True when an open per-generator compensation-plan override exists (the
   *  generator is on a custom plan rather than the program default). */
  hasCustomPlan?: boolean;
}

export interface LeadGeneratorDetailDto {
  id: string;
  displayName: string;
  email: string;
  phone?: string | null;
  status: LeadGeneratorStatus;
  portalUserId?: string | null;
  hiredAtUtc?: string | null;
  notes?: string | null;
  isArchived: boolean;
  archivedAtUtc?: string | null;
  createdAt: string;
  modifiedAt?: string | null;
  /** True when an open per-generator compensation-plan override exists (the
   *  generator is on a custom plan rather than the program default). */
  hasCustomPlan?: boolean;
}

export interface LeadGeneratorStatsDto {
  total: number;
  byStatus: Record<LeadGeneratorStatus, number>;
}

/** A manager's coaching/feedback note. Read model only — there is no staff
 *  feedback-WRITE endpoint yet (the generator reads these in their own portal
 *  feed via `api/portal/lead-generator/feedback`). */
export interface LeadGenFeedbackDto {
  id: string;
  leadGeneratorId: string;
  authorUserId: string;
  body: string;
  category?: string | null;
  weekOf?: string | null;
  acknowledgedAtUtc?: string | null;
  createdAt: string;
}

export interface LeadGeneratorPortalInviteResultDto {
  leadGeneratorId: string;
  portalUserId: string;
  email: string;
  isExistingAccount: boolean;
  status: LeadGeneratorStatus;
}

export interface LeadGeneratorListQuery extends PagedQuery {
  status?: LeadGeneratorStatus;
  /** Only archived rows when true; omitted/false excludes archived (default). */
  archived?: boolean;
}

export interface UpsertLeadGeneratorRequest {
  displayName: string;
  email: string;
  phone?: string | null;
  hiredAtUtc?: string | null;
  notes?: string | null;
}

export const leadGeneratorsApi = {
  list: (query?: LeadGeneratorListQuery) =>
    api.get<PagedResult<LeadGeneratorSummaryDto>>("/lead-generators", query),
  stats: (query?: LeadGeneratorListQuery) =>
    api.get<LeadGeneratorStatsDto>("/lead-generators/stats", query),
  get: (id: string) => api.get<LeadGeneratorDetailDto>(`/lead-generators/${id}`),
  create: (body: UpsertLeadGeneratorRequest) =>
    api.post<LeadGeneratorDetailDto>("/lead-generators", body),
  update: (id: string, body: UpsertLeadGeneratorRequest) =>
    api.put<LeadGeneratorDetailDto>(`/lead-generators/${id}`, body),
  setStatus: (id: string, status: LeadGeneratorStatus) =>
    api.patch<LeadGeneratorDetailDto>(`/lead-generators/${id}/status`, { status }),
  archive: (id: string) =>
    api.post<LeadGeneratorDetailDto>(`/lead-generators/${id}/archive`),
  unarchive: (id: string) =>
    api.post<LeadGeneratorDetailDto>(`/lead-generators/${id}/unarchive`),
  /** Provision (or re-assert) the external portal login for a roster generator. */
  portalInvite: (id: string) =>
    api.post<LeadGeneratorPortalInviteResultDto>(`/lead-generators/${id}/portal-invite`),
};
